import { useState } from 'react'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { motion } from 'framer-motion'
import { useIntlayer } from 'react-intlayer'
import { completeOnboarding } from '../../wiring'

type Props = {
	onComplete: () => void
	onLoadingChange?: (loading: boolean) => void
}

const CreateTeamStep = ({ onComplete, onLoadingChange }: Props) => {
	const [name, setName] = useState('')
	const [error, setError] = useState<string | null>(null)
	const content = useIntlayer('create-team-step')

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (name.trim().length < 2) {
			setError(content.errorMinLength.value)
			return
		}

		onLoadingChange?.(true)
		try {
			await completeOnboarding(name.trim())
			onComplete()
		} catch {
			setError('Something went wrong. Please try again.')
		} finally {
			onLoadingChange?.(false)
		}
	}

	return (
		<div className='space-y-4'>
			<motion.h1
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4, delay: 0.1 }}
				className='text-lg lg:text-xl font-serif'
			>
				{content.title}
			</motion.h1>
			<motion.p
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4, delay: 0.2 }}
				className='text-sm text-muted-foreground leading-relaxed'
			>
				{content.description}
			</motion.p>

			<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.35, delay: 0.3 }}>
				<form id='create-team-form' onSubmit={handleSubmit} className='space-y-4'>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='teamName' className='text-xs text-primary font-normal'>
							{content.companyNameLabel}
						</Label>
						<Input
							id='teamName'
							autoFocus
							placeholder={content.companyNamePlaceholder.value}
							autoComplete='off'
							value={name}
							onChange={e => {
								setName(e.target.value)
								setError(null)
							}}
							className='bg-secondary border-border text-foreground placeholder:text-muted-foreground'
						/>
					</div>
					{error && <p className='text-xs text-destructive'>{error}</p>}
				</form>
			</motion.div>
		</div>
	)
}

export { CreateTeamStep }
